import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import {
  EmailIssue,
  EmailIssueFilters,
  EmailIssueStats,
  EmailIssuesService
} from '../services/email-issues.service';
import { AuthService } from '../../../shared/service/auth.service';
import { User, UserService } from '../../../shared/service/user.service';

interface InsurerSender {
  email: string;
  label: string;
}

@Component({
  selector: 'app-email-issues-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    TableModule,
    TagModule,
    TooltipModule,
    DialogModule
  ],
  templateUrl: './email-issues-dashboard.component.html',
  styleUrl: './email-issues-dashboard.component.css'
})
export class EmailIssuesDashboardComponent implements OnInit {
  isAdmin = false;
  stats: EmailIssueStats | null = null;
  problemCount = 0;
  inProgressCount = 0;
  treatedCount = 0;
  ignoredCount = 0;
  issues: EmailIssue[] = [];
  loading = false;
  error: string | null = null;

  users: User[] = [];
  userOptions: { label: string; value: number | null }[] = [{ label: 'Tous', value: null }];
  selectedUserId: number | null = null;
  private usersById = new Map<number, User>();

  showEmailContentDialog = false;
  selectedEmailForView: EmailIssue | null = null;
  private readonly contentPreviewMaxLength = 120;

  first = 0;
  rows = 25;
  totalRecords = 0;

  searchTerm = '';
  selectedClassification: string | null = null;
  selectedStatus: string | null = null;
  selectedClientExists: boolean | null = null;

  insurerSenders: InsurerSender[] = [
    { email: 'No_Reply@message.april.com', label: 'APRIL' },
    { email: 'noreply@april.com', label: 'April' },
    { email: 'gestion@utwin.fr', label: 'UTWIN' },
    { email: 'dip@metlife.fr', label: 'METLIFE' },
    { email: 'accueil.intermediaires@swisslife.fr', label: 'SWISSLIFE' },
    { email: 'oav-cle@kereisfrance.com', label: 'KEREIS' },
    { email: 'gestion@afi-esca.com', label: 'AFI ESCA' },
    { email: 'empruntea@sollyazar.com', label: 'SOLLY AZAR' },
    { email: 'sollyazar@multi-impact.com', label: 'SOLLY AZAR' },
    { email: 'gestion@zenioo.com', label: 'ZENIOO' },
    { email: 'souscription-zenioo@mncap.fr', label: 'MNCAP' },
    { email: 'emprunteurs.zenioo@verlingue.fr', label: 'VERLINGUE' },
    { email: 'medical@multi-impact.com', label: 'MULTI IMPACT' },
    { email: 'gestion.courtiers@alptis.fr', label: 'ALPTIS' },
    { email: 'production@ugip.org', label: 'UGIP' },
    { email: 'demandes.courtiers@malakoffhumanis.com', label: 'MALAKOFF HUMANIS' },
    { email: 'lifesquare@multi-impact.com', label: 'LIFESQUARE' },
    { email: 'gestionemprunteurs@suravenir.fr', label: 'SURAVENIR' }
  ];

  private noActionMessages = new Set([
    'bonjour',
    'bonsoir',
    'salut',
    'hello',
    'hi',
    'merci',
    'merci beaucoup',
    'ok',
    'd accord',
    'daccord',
    'bien recu',
    'recu',
    'c est note',
    'cest note'
  ]);

  openProject = (issue: EmailIssue): void => {
    if (!issue.project_id) {
      return;
    }

    this.router.navigate(['/cac/comparaison'], {
      queryParams: { loadProject: issue.project_id }
    });
  };

  canOpenProject = (issue: EmailIssue): boolean => {
    return Boolean(
      issue.project_id && issue.client_exists && !this.getInsurerOriginLabel(issue)
    );
  };

  getOriginLabel = (issue: EmailIssue): string => {
    const insurerOrigin = this.getInsurerOriginLabel(issue);
    if (insurerOrigin) {
      return insurerOrigin;
    }

    return issue.client_exists ? 'Client connu' : 'Origine inconnue';
  };

  getOriginSeverity = (issue: EmailIssue): string => {
    if (this.getInsurerOriginLabel(issue)) {
      return 'secondary';
    }

    return issue.client_exists ? 'info' : 'warning';
  };

  getOriginDetail = (issue: EmailIssue): string => {
    const insurerOrigin = this.getInsurerOriginLabel(issue);
    if (insurerOrigin) {
      return `${insurerOrigin} - ${issue.from_email}`;
    }

    return issue.client_name || issue.from_email || '-';
  };

  classificationOptions = [
    { label: 'Tous', value: null },
    { label: 'Avec probleme', value: 'probleme' },
    { label: 'Sans probleme', value: 'sans_probleme' }
  ];

  statusOptions = [
    { label: 'Tous', value: null },
    { label: 'Nouveau', value: 'nouveau' },
    { label: 'En cours', value: 'en_cours' },
    { label: 'Traite', value: 'traite' },
    { label: 'Ignore', value: 'ignore' }
  ];

  clientOptions = [
    { label: 'Tous', value: null },
    { label: 'Client connu', value: true },
    { label: 'Autre origine', value: false }
  ];

  constructor(
    private emailIssuesService: EmailIssuesService,
    private router: Router,
    private authService: AuthService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    if (this.isAdmin) {
      this.loadUsers();
    }
    this.refresh();
  }

  refresh(): void {
    this.loadStats();
    this.loadIssues();
  }

  loadStats(): void {
    const userId = this.isAdmin ? this.selectedUserId : null;
    this.emailIssuesService.getStats(userId).subscribe({
      next: (stats) => {
        this.stats = stats;
        const statusStats = stats as EmailIssueStats & {
          in_progress_count?: number;
          treated_count?: number;
          ignored_count?: number;
        };

        this.problemCount = stats.problem_count || 0;
        this.inProgressCount = statusStats.in_progress_count || 0;
        this.treatedCount = statusStats.treated_count || 0;
        this.ignoredCount = statusStats.ignored_count || 0;
      },
      error: (error) => {
        console.error('Erreur stats emails:', error);
      }
    });
  }

  loadUsers(): void {
    this.userService.getUsers().subscribe({
      next: (users) => {
        this.users = users || [];
        this.usersById.clear();
        this.users.forEach((user) => this.usersById.set(user.id, user));
        this.userOptions = [
          { label: 'Tous', value: null },
          ...this.users.map((user) => ({
            label: this.getUserLabel(user),
            value: user.id
          }))
        ];
      },
      error: (error) => {
        console.error('Erreur chargement utilisateurs:', error);
      }
    });
  }

  loadIssues(): void {
    this.loading = true;
    this.error = null;

    this.emailIssuesService.getIssues(this.rows, this.first, this.getFilters()).subscribe({
      next: (response) => {
        this.issues = response.items.filter(
          (issue) => !this.isNoActionEmail(issue) && !this.isBrokinsInternalEmail(issue)
        );
        this.totalRecords = Math.max(response.total - (response.items.length - this.issues.length), 0);
        this.loading = false;
      },
      error: (error) => {
        console.error('Erreur chargement emails:', error);
        this.error = error?.error?.detail || 'Impossible de charger les emails.';
        this.loading = false;
      }
    });
  }

  onLazyLoad(event: any): void {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? this.rows;
    this.loadIssues();
  }

  applyFilters(): void {
    this.first = 0;
    this.loadStats();
    this.loadIssues();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.selectedClassification = null;
    this.selectedStatus = null;
    this.selectedClientExists = null;
    this.selectedUserId = null;
    this.applyFilters();
  }

  markStatus(issue: EmailIssue, status: string): void {
    this.emailIssuesService.updateStatus(issue.id, status).subscribe({
      next: (updatedIssue) => {
        this.issues = this.issues.map((item) =>
          item.id === updatedIssue.id ? updatedIssue : item
        );
        this.loadStats();
      },
      error: (error) => {
        console.error('Erreur statut email:', error);
      }
    });
  }

  getFilters(): EmailIssueFilters {
    return {
      search: this.searchTerm,
      classification: this.selectedClassification,
      status: this.selectedStatus,
      client_exists: this.selectedClientExists,
      user_id: this.isAdmin ? this.selectedUserId : null
    };
  }

  getOwnerLabel(issue: EmailIssue): string {
    if (!issue.user_id) {
      return 'Non attribue';
    }

    const user = this.usersById.get(issue.user_id);
    if (!user) {
      return `Utilisateur #${issue.user_id}`;
    }

    return this.getUserLabel(user);
  }

  private getUserLabel(user: User): string {
    const username = (user.username || '').trim();
    const email = (user.email || '').trim();

    if (username && email && username.toLowerCase() !== email.toLowerCase()) {
      return `${username} (${email})`;
    }

    return email || username || `Utilisateur #${user.id}`;
  }

  formatDate(value?: string | null): string {
    if (!value) {
      return '-';
    }

    return new Date(value).toLocaleDateString('fr-FR', {
      timeZone: 'Europe/Paris',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getInsurerSender(issue: EmailIssue): InsurerSender | null {
    const senderEmail = this.normalizeEmail(issue.from_email);

    return this.insurerSenders.find(
      (sender) => this.normalizeEmail(sender.email) === senderEmail
    ) || null;
  }

  private getInsurerOriginLabel(issue: EmailIssue): string | null {
    const insurerSender = this.getInsurerSender(issue);
    if (insurerSender) {
      return insurerSender.label;
    }

    const source = (issue.source || '').trim();
    if (!source || source === 'gmail_n8n') {
      return null;
    }

    return source;
  }

  private normalizeEmail(value?: string | null): string {
    const rawValue = (value || '').trim().toLowerCase();
    const emailMatch = rawValue.match(/<([^>]+)>/);

    return emailMatch ? emailMatch[1].trim() : rawValue;
  }

  private isBrokinsInternalEmail(issue: EmailIssue): boolean {
    return this.normalizeEmail(issue.from_email).endsWith('@brokins.fr');
  }

  private isNoActionEmail(issue: EmailIssue): boolean {
    const parts = [issue.subject, issue.snippet, issue.body]
      .map((value) => this.normalizeText(value))
      .filter(Boolean);
    const uniqueParts = Array.from(new Set(parts));
    const text = uniqueParts.join(' ');

    return this.noActionMessages.has(text);
  }

  private normalizeText(value?: string | null): string {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ');
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'traite':
        return 'success';
      case 'en_cours':
        return 'info';
      case 'ignore':
        return 'secondary';
      default:
        return 'warning';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'traite':
        return 'Traite';
      case 'en_cours':
        return 'En cours';
      case 'ignore':
        return 'Ignore';
      default:
        return 'Nouveau';
    }
  }

  getSenderLabel(issue: EmailIssue): string {
    return issue.from_name ? `${issue.from_name} <${issue.from_email}>` : issue.from_email;
  }

  getFullEmailContent(issue: EmailIssue): string {
    const content = (issue.body || issue.snippet || '').trim();
    return content || 'Aucun contenu disponible pour cet email.';
  }

  getContentPreview(issue: EmailIssue): string {
    const content = this.getFullEmailContent(issue);
    if (content === 'Aucun contenu disponible pour cet email.') {
      return '-';
    }

    if (content.length <= this.contentPreviewMaxLength) {
      return content;
    }

    return `${content.slice(0, this.contentPreviewMaxLength).trim()}…`;
  }

  isContentExpandable(issue: EmailIssue): boolean {
    const content = (issue.body || issue.snippet || '').trim();
    return content.length > this.contentPreviewMaxLength;
  }

  openEmailContentDialog(issue: EmailIssue): void {
    this.selectedEmailForView = issue;
    this.showEmailContentDialog = true;
  }

  closeEmailContentDialog(): void {
    this.showEmailContentDialog = false;
    this.selectedEmailForView = null;
  }
}
